import { useEffect, useState } from 'react'
import {
  EMPTY_NOTIFY, noteAsked, noteDone, shouldAskNotify, type NotifyState,
} from '../../../shared/notify-rules.ts'
import { authMode } from '../lib/auth'
import { enablePush, pushAsked, pushSupported } from '../lib/push'
import { api } from '../lib/api'
import { haptic } from '../lib/telegram'
import { useSirr } from '../store'

const STATE_KEY = 'sirr_notify_ask'

/**
 * Состояние показов лежит в localStorage одной записью. Правило, по которому
 * оно читается, — общее с нативом (`shared/notify-rules.ts`) и покрыто тестом:
 * ошибка в такой бухгалтерии проявляется только жалобой живого человека.
 */
function loadState(): NotifyState {
  try {
    return { ...EMPTY_NOTIFY, ...(JSON.parse(localStorage.getItem(STATE_KEY) ?? '{}') as Partial<NotifyState>) }
  } catch {
    return { ...EMPTY_NOTIFY }
  }
}

function saveState(s: NotifyState): void {
  localStorage.setItem(STATE_KEY, JSON.stringify(s))
}

/**
 * Предложение включить напоминания — ПОСЛЕ первого расклада, а не при запуске.
 *
 * Момент выбран не случайно. При запуске человек ещё не знает, что мы такое, и
 * разрешение на уведомления от незнакомого приложения отклоняют не глядя; а
 * отклонённое разрешение браузер второй раз не спросит — предложить его снова
 * будет уже нечем. После первого расклада ценность понятна: карта дня меняется
 * каждый день, и напоминание — единственный способ об этом узнать. Замерено на
 * боевом: из 21 человека разрешение включил ОДИН, и наш прошлый момент
 * («первый запуск установленного приложения») почти ни до кого не доходил.
 *
 * Требования к площадке проверяет `pushSupported()`, а не отдельная проверка
 * установки: в Safari без установки на экран `PushManager` не существует
 * вовсе, и условие выполняется само. На Android push работает и во вкладке,
 * поэтому запрещать её незачем.
 *
 * В Telegram не показываем: там уведомления и так включены по умолчанию
 * (opt-out), спрашивать нечего.
 */
export function NotifyAsk() {
  const { t, profile, update } = useSirr()
  const [state, setState] = useState(loadState)
  const [hidden, setHidden] = useState(false)
  const [busy, setBusy] = useState(false)

  // Разрешение могли выдать в другом месте — тогда предлагать больше нечего.
  useEffect(() => {
    if (pushAsked()) setHidden(true)
  }, [])

  // Показ засчитывается сразу, а не по кнопке «Позже»: человек может просто
  // уйти с экрана, и это тоже «не сейчас». Иначе одна и та же попытка
  // показывалась бы бесконечно.
  // Считаем не только расклады, но и открытую карту дня. Карту открывают
  // почти все, а до полного расклада доходит половина — замерено на боевом
  // 2026-08-27: из 21 человека, зашедшего ровно один день, онбординг прошли
  // все, а расклад сделали десять. Требуя расклад, мы не показывали
  // предложение как раз тем, кто уходил первым. И напоминаем мы именно про
  // карту дня — то есть про то, что человек только что и увидел.
  const readings = profile.history.length + Object.keys(profile.dayFlipped ?? {}).length
  const visible = !hidden && !busy && shouldAskNotify(state, readings, Date.now())
  useEffect(() => {
    if (!visible) return
    const next = noteAsked(state, readings, Date.now())
    setState(next)
    saveState(next)
    // Считаем показ один раз за появление, а не на каждый рендер.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visible])

  if (authMode !== 'pwa' || !pushSupported() || pushAsked()) return null
  if (profile.notify) return null
  if (!visible) return null

  /** «Позже» — просто убрать с глаз: показ уже засчитан выше. */
  const close = () => setHidden(true)

  /** «Включить» — дальше решает система, и спрашивать больше нечем. */
  const stop = () => {
    const next = noteDone(state)
    setState(next)
    saveState(next)
    setHidden(true)
  }

  const turnOn = async () => {
    haptic.select()
    setBusy(true)
    try {
      if ((await enablePush()).ok) {
        update({ notify: true })
        void api.setNotify(true)
      }
    } finally {
      stop()
      setBusy(false)
    }
  }

  return (
    <div className="notify-ask">
      <span className="notify-ask__sym">✦</span>
      <div className="notify-ask__text">{t('notifyAskTitle')}</div>
      <button type="button" className="notify-ask__go" onClick={() => void turnOn()}>
        {t('notifyAskBtn')}
      </button>
      <button type="button" className="notify-ask__later" onClick={close}>
        {t('notifyLater')}
      </button>
    </div>
  )
}
